import { WS_API_BASE_URL } from "@gameApp/constants";
import { tradesMapList } from "@gameApp/trades/mappers";
import type {
  GlobalCurrency,
  Timeframes,
  TradesDataItem,
  TradesListenersMeta,
  TradesListenParams,
  TradesParams,
  TradesPricesMap,
} from "@gameApp/trades/types";
import { jsonSafeParse } from "@gameApp/utils/json";

export function tradesMergeItemsWithMap(
  nextItems: TradesDataItem[],
  prevMap: TradesPricesMap,
): TradesPricesMap {
  const nextMap = new Map(prevMap);

  return nextItems.reduce((resultMap, item) => {
    if (resultMap.has(item.currencyId)) {
      const prevItem = resultMap.get(item.currencyId);

      const nextItem = {
        ...prevItem,
        time: item.time,
        price: {
          ...prevItem?.price,
          ...item.price,
        },
        priceChange: {
          ...prevItem?.priceChange,
          ...item.priceChange,
        },
      } as TradesDataItem;

      resultMap.set(item.currencyId, nextItem);
    } else {
      resultMap.set(item.currencyId, item);
    }

    return resultMap;
  }, nextMap);
}

interface ListenersMetaParams {
  listenParams: Partial<TradesListenParams>;
  meta: TradesListenersMeta;
  operation: "addition" | "subtraction";
}

export function tradesUpdateListenersMeta(
  params: ListenersMetaParams,
): TradesListenersMeta {
  const { listenParams, meta, operation } = params;
  const { currencyId, changeTimeframe, changeCurrency } = listenParams;

  if (typeof currencyId === "number") {
    const prevCount = meta.currencyIds.get(currencyId) || 0;

    const nextCount =
      operation === "addition" ? prevCount + 1 : Math.max(0, prevCount - 1);

    meta.currencyIds.set(currencyId, nextCount);
  }

  if (typeof changeTimeframe === "string") {
    const prevCount = meta.changeTimeframes.get(changeTimeframe) || 0;
    meta.changeTimeframes.set(changeTimeframe, prevCount + 1);
  }

  if (typeof changeCurrency === "string") {
    const prevCount = meta.changeCurrencies.get(changeCurrency) || 0;
    meta.changeCurrencies.set(changeCurrency, prevCount + 1);
  }

  return {
    currencyIds: meta.currencyIds,
    changeTimeframes: meta.changeTimeframes,
    changeCurrencies: meta.changeCurrencies,
  };
}

export function tradesGetStreamParams(meta: TradesListenersMeta): TradesParams {
  const { currencyIds, changeTimeframes, changeCurrencies } = meta;

  const nextCurrencyIds: number[] = Array.from(currencyIds.entries()).reduce(
    (result, [item, count]) => {
      if (count > 0) {
        return result.concat([item]);
      }

      return result;
    },
    [] as number[],
  );

  const nextChangeTimeframes: Timeframes[] = Array.from(
    changeTimeframes.entries(),
  ).reduce((result, [item, count]) => {
    if (count > 0) {
      return result.concat([item]);
    }

    return result;
  }, [] as Timeframes[]);

  const hasChangeTimeframes = nextChangeTimeframes.length > 0;

  const nextChangeCurrencies: GlobalCurrency[] = Array.from(
    changeCurrencies.entries(),
  ).reduce((result, [item, count]) => {
    if (count > 0) {
      return result.concat([item]);
    }

    return result;
  }, [] as GlobalCurrency[]);

  const hasChangeCurrencies = nextChangeCurrencies.length > 0;
  const hasChanges = hasChangeTimeframes && hasChangeCurrencies;

  return {
    withChanges: hasChanges,
    currencyIds: nextCurrencyIds,
    changeTimeframes: nextChangeTimeframes,
    changeCurrencies: nextChangeCurrencies,
  };
}

export function tradesGetStreamUrl(params: TradesParams): string {
  const { currencyIds, withChanges, changeTimeframes, changeCurrencies } =
    params;

  if (currencyIds.length === 0) {
    return "";
  }

  const searchParams = Object.entries({
    currencies: currencyIds.sort().join(","),
    change: withChanges ? "true" : "false",
    timeframes: changeTimeframes.sort().join(","),
    changeCurrencies: changeCurrencies.sort().join(","),
  })
    .map(([key, value]) => {
      return `${key}=${value}`;
    })
    .join("&");

  const url = new URL(`${WS_API_BASE_URL}/trades/tradePriceUpdates`);
  url.search = searchParams.toString();

  return url.toString();
}

export function tradesOpenStream(
  url: string,
  onChange: (nextItems: TradesDataItem[]) => void,
): EventSource {
  const eventSource = new window.EventSource(url);

  eventSource.onerror = () => {
    // silence reconnection errors
  };

  eventSource.onmessage = (e: MessageEvent) => {
    const rawItems = jsonSafeParse<unknown[]>({
      text: e.data,
      defaultValue: [],
    });

    const nextItems = tradesMapList(rawItems);

    if (nextItems.length > 0) {
      onChange(nextItems);
    }
  };

  return eventSource;
}
